
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { ProductGrid } from "@/components/ProductGrid";
import { Footer } from "@/components/Footer";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FEATURED_PRODUCTS } from "@/data/products";
import { useState, useMemo, useCallback } from "react";
import { SearchCommand } from "@/components/SearchCommand";
import { useQuery } from "@tanstack/react-query";
import { SortOption } from "@/components/search/searchUtils";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { ProductCategoryRow } from "@/components/ProductCategoryRow";
import { Product } from "@/types/product";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

// Simulate fetching products from an API
const fetchProducts = () => {
  return new Promise((resolve) => {
    // Simulate network delay
    setTimeout(() => {
      resolve(FEATURED_PRODUCTS);
    }, 1500);
  });
};

const Index = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("name-asc");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  
  // Reference to the search bar container to track when it exits viewport
  const [searchBarRef, isSearchBarVisible] = useIntersectionObserver({
    threshold: 0,
    rootMargin: "-1px 0px 0px 0px" // Trigger as soon as the element touches the top
  });
  
  // Use TanStack Query for data fetching with loading state
  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProducts(),
  });

  // Memoize filtered and sorted products
  const getFilteredAndSortedProducts = useMemo(() => {
    if (!products) return [];
    
    let filtered = [...FEATURED_PRODUCTS];

    if (activeTab === "prescription") {
      filtered = filtered.filter(p => p.prescription);
    } else if (activeTab === "otc") {
      filtered = filtered.filter(p => !p.prescription);
    }
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "name-asc": return a.title.localeCompare(b.title);
        case "name-desc": return b.title.localeCompare(a.title);
        case "price-asc": return a.price - b.price;
        case "price-desc": return b.price - a.price;
        default: return 0;
      }
    });
  }, [products, activeTab, sortBy]);
  
  // Group products for display
  const { frequentlyBought, byCategory, productsForCategoryView } = useMemo(() => {
    const baseProducts = getFilteredAndSortedProducts;
    
    const frequentlyBought = baseProducts.filter(p => p.frequentlyBought);
    
    const byCategory = baseProducts.reduce((acc, product) => {
      if (!product.frequentlyBought) {
        if (!acc[product.category]) {
          acc[product.category] = [];
        }
        acc[product.category].push(product);
      }
      return acc;
    }, {} as Record<string, Product[]>);

    const productsForCategoryView = selectedCategory ? baseProducts.filter(p => p.category === selectedCategory) : [];
    
    return { frequentlyBought, byCategory, productsForCategoryView };
  }, [getFilteredAndSortedProducts, selectedCategory]);

  // Pre-calculate these values to avoid recalculation on each render
  const prescriptionCount = useMemo(() => 
    FEATURED_PRODUCTS.filter(p => p.prescription).length, 
    []
  );
  
  const otcCount = useMemo(() => 
    FEATURED_PRODUCTS.filter(p => !p.prescription).length, 
    []
  );

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header 
        showSearchInHeader={!isSearchBarVisible}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />
      <Hero />
      
      <main className="flex-1">
        <div 
          ref={searchBarRef}
          className="sticky top-16 border-b bg-background/95 backdrop-blur shadow-sm z-10"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-3">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-auto">
                <TabsList className="p-1 rounded-full">
                  <TabsTrigger value="all" className="rounded-full text-xs">
                    All Products
                    <Badge variant="secondary" className="ml-2 rounded-full text-xs">
                      {FEATURED_PRODUCTS.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="prescription" className="rounded-full text-xs">
                    Prescription Only
                    <Badge variant="secondary" className="ml-2 rounded-full text-xs">
                      {prescriptionCount}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="otc" className="rounded-full text-xs">
                    Over the Counter
                    <Badge variant="secondary" className="ml-2 rounded-full text-xs">
                      {otcCount}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div 
                className="w-full md:w-auto transition-opacity duration-300" 
                style={{ opacity: isSearchBarVisible ? 1 : 0 }}
              >
                <SearchCommand
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  filterMenuOpen={filterMenuOpen}
                  setFilterMenuOpen={setFilterMenuOpen}
                  sortMenuOpen={sortMenuOpen}
                  setSortMenuOpen={setSortMenuOpen}
                />
              </div>
            </div>
          </div>
        </div>

        <section className="py-8">
          <div className="container mx-auto px-4">
            {error ? (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  There was a problem loading the products. Please try again later.
                </AlertDescription>
              </Alert>
            ) : isLoading ? (
              <div className="space-y-8">
                {Array(2).fill(null).map((_, index) => (
                  <div key={index}>
                    <Skeleton className="h-8 w-1/3 mb-4" />
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Array(4).fill(null).map((_, i) => (
                        <ProductCardSkeleton key={i} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedCategory ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{selectedCategory}</h2>
                  <Button variant="outline" onClick={() => setSelectedCategory(null)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to all categories
                  </Button>
                </div>
                <ProductGrid 
                  products={productsForCategoryView} 
                  isLoading={isLoading}
                />
              </>
            ) : (
              <div className="space-y-4">
                <ProductCategoryRow
                  title="Frequently Bought"
                  products={frequentlyBought}
                  limit={8}
                />

                {Object.entries(byCategory).map(([category, catProducts]) => (
                  <ProductCategoryRow
                    key={category}
                    title={category}
                    products={catProducts}
                    onViewAll={() => setSelectedCategory(category)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Index;
